import React from "react";
import { motion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Trash2, Plus } from "lucide-react";
import { getTransactions, deleteTransaction, setHoldingGoal } from "../lib/api";
import { Badge } from "./Primitives";
import { POS, NEG } from "../lib/format";

// Per-holding transaction history with delete, goal link and add buy/sell.
export default function TransactionsModal({ position, goals = [], onClose, onAdd, onChanged }) {
  const qc = useQueryClient();
  const key = ["transactions", position.instrument_id];
  const { data } = useQuery({ queryKey: key, queryFn: () => getTransactions(position.instrument_id) });
  const cur = position.currency === "USD" ? "$" : "₹";
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); onChanged(); };
  const remove = async (id) => { if (window.confirm("Delete this transaction?")) { await deleteTransaction(id); refresh(); } };
  const txns = data?.transactions || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[55] flex items-center justify-center p-4" style={{ background: "rgba(4,7,13,0.75)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        onClick={(e) => e.stopPropagation()} className="ao-card w-full max-w-lg p-6 max-h-[92dvh] overflow-y-auto" data-testid="txn-modal">
        <div className="flex items-start justify-between mb-1">
          <div className="min-w-0"><div className="text-lg font-semibold truncate">{position.name}</div>
            <div className="font-mono text-[11px] text-[#64748b]">{position.symbol} · {position.qty} units @ avg {cur}{position.avg_cost}</div></div>
          <button data-testid="txn-modal-close" onClick={onClose} className="text-[#64748b] hover:text-[#f8fafc]"><X className="w-5 h-5" /></button>
        </div>
        <label className="overline block mt-4">Goal</label>
        <select data-testid="txn-goal" defaultValue={position.goal_id || ""} onChange={async (e) => { await setHoldingGoal(position.instrument_id, e.target.value || null); onChanged(); }}
          className="w-full mt-1 bg-[#0f1420] border border-[#2d3c59] rounded-lg px-3 py-2 text-sm text-[#f8fafc]">
          <option value="">— No goal (general) —</option>
          {goals.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <div className="flex items-center justify-between mt-5 mb-2"><div className="overline">Transactions</div>
          <button data-testid="txn-add-btn" onClick={onAdd} className="flex items-center gap-1 text-xs text-[#00e5ff]"><Plus className="w-3.5 h-3.5" /> Add buy / sell</button></div>
        <div className="space-y-1.5" data-testid="txn-list">
          {txns.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-lg border border-[#1e293b] px-3 py-2 font-mono text-xs" data-testid={`txn-row-${t.id}`}>
              <Badge color={t.side === "buy" ? POS : NEG}>{t.side.toUpperCase()}</Badge>
              <span className="text-[#94a3b8] w-20 shrink-0">{t.date}</span>
              <span className="text-[#f8fafc] flex-1 truncate">{t.qty} × {cur}{t.price}{t.fees ? ` + ${cur}${t.fees}` : ""}</span>
              <button data-testid={`txn-delete-${t.id}`} onClick={() => remove(t.id)} className="text-[#64748b] hover:text-[#ff3b30]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {!txns.length && <div className="text-xs text-[#64748b]">No transactions.</div>}
        </div>
      </motion.div>
    </motion.div>
  );
}
